import * as R from 'ramda';
import {
  all,
  put,
  call,
  fork,
  cancel,
  select,
  cancelled,
  takeLatest,
} from 'redux-saga/effects';
// components
import { openLoader, closeLoader } from '../../components/loader/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature dashboards
import * as A from './actions';
import * as C from './constants';
import { makeSelectDashboardsStore } from './selectors';
//////////////////////////////////////////////////

function* getIndividualChartRequest({ payload }) {
  try {
    const { chartType, numberRange } = payload;

    const store = yield select(makeSelectDashboardsStore());

    const { filters, dashboardType } = store;

    const { dateTo, dateFrom, enterprise } = filters;

    const options = {
      data: {
        dateTo,
        dateFrom,
        chartType,
        numberRange,
        type: dashboardType,
        enterpriseGuid: R.or(enterprise, G.getAmousCurrentBranchGuidFromWindow()),
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.statisticDashboardChart, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getIndividualChartSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'getIndividualChartRequest fail', true);
    }
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'getIndividualChartRequest exception');
  }
}

function* getChartByFiltersRequest({ payload }) {
  try {
    const { chartType } = payload;

    yield put(A.setIndividualChartLoadingState({ chartType }));

    yield call(getIndividualChartRequest, { payload});
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'getChartByFiltersRequest exception');
  }
}

function* setIndividualChartLoading(group) {
  try {
    for (const chartType of group) {
      yield put(A.setIndividualChartLoadingState({ chartType }));
    }
  } catch (error) {
    yield call(G.handleException, error, 'setIndividualChartLoader exception');
  }
}

function* getDashboardIndividualGroup(group) {
  try {
    const groupBunchCall = [];

    for (const chartType of group) {
      groupBunchCall.push(call(getIndividualChartRequest, { payload: { chartType }}));
    }

    yield all(groupBunchCall);
  } catch (error) {
    yield call(G.handleException, error, 'getDashboardIndividualGroup exception');
  }
}

let groupsRequestTask;

function* getDashboardGroupsRequest(groups) {
  try {
    for (const group of groups) {
      yield call(getDashboardIndividualGroup, group);
    }

    groupsRequestTask = null;
  } finally {
    if (yield cancelled()) groupsRequestTask = null;
  }
}

function* getDashboardGroups() {
  try {
    if (groupsRequestTask) {
      // cancel previous task on change filters or switch tab
      yield cancel(groupsRequestTask);
    }

    const store = yield select(makeSelectDashboardsStore());

    const { dashboardType } = store;

    const groups = R.prop(dashboardType, C.dashboardGroupCallMap);

    if (G.isNilOrEmpty(groups)) return yield put(closeLoader());

    // set loading state before a group call
    for (const group of groups) {
      yield call(setIndividualChartLoading, group);
    }

    // fork groups call to make it cancelable
    groupsRequestTask = yield fork(getDashboardGroupsRequest, groups);
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'getDashboardGroups exception');
  }
}

function* generateExcelWithChartData({ payload }) {
  try {
    yield put(openLoader());

    const store = yield select(makeSelectDashboardsStore());

    const { filters, dashboardType } = store;
    const { dateTo, dateFrom, enterprise } = filters;

    const options = {
      resType: 'arraybuffer',
      data: {
        dateTo,
        dateFrom,
        numberRange: null,
        chartType: payload,
        type: dashboardType,
        enterpriseGuid: R.or(enterprise, G.getAmousCurrentBranchGuidFromWindow()),
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.statisticDashboardChartExport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'info', 'messages:downloading-file');
    } else {
      yield call(G.handleFailResponse, res, 'generateExcelWithChartData fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'generateExcelWithChartData exception');
  }
}

function* getNormalizedCurrencyRequest() {
  try {
    const store = yield select(makeSelectDashboardsStore());

    const { filters, normalizedCurrency } = store;

    const { enterprise } = filters;

    const enterpriseGuid = R.or(enterprise, G.getAmousCurrentBranchGuidFromWindow());

    if (G.isNotNilAndNotEmpty(G.getPropFromObject(enterpriseGuid, normalizedCurrency))) return;

    const options = { params: { enterpriseGuid } };

    const res = yield call(sendRequest, 'get', endpointsMap.branchNormalizedCurrency, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getNormalizedCurrencySuccess({ enterpriseGuid, data: R.or(data, GC.DEFAULT_UI_CURRENCY) }));
    } else {
      yield call(G.handleFailResponse, res, 'getNormalizedCurrencyRequest fail', true);
    }
  } catch (error) {
    yield call(G.handleException, error, 'getNormalizedCurrencyRequest exception');
  }
}

function* handleVisitPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_DASHBOARDS_PAGE);

    yield put(A.getNormalizedCurrencyRequest());
    yield put(A.getDashboardGroups());

    break;
  }
}

export function* dashboardWatcherSaga() {
  yield takeLatest(GC.VISIT_DASHBOARDS_PAGE, handleVisitPageSaga);
  //
  yield takeLatest(A.getDashboardGroups, getDashboardGroups);
  yield takeLatest(A.setDateFiltersToStore, getDashboardGroups);
  yield takeLatest(A.setDashboardTypeToStore, getDashboardGroups);
  yield takeLatest(A.setEnterpriseFilterToStore, getDashboardGroups);
  yield takeLatest(A.getChartByFiltersRequest, getChartByFiltersRequest);
  yield takeLatest(A.generateExcelWithChartData, generateExcelWithChartData);
  yield takeLatest(A.getNormalizedCurrencyRequest, getNormalizedCurrencyRequest);
}

export default dashboardWatcherSaga;
